
import {
    _CLAIMS_AUX_TABLES_R,
    _CLAIMS_EQP_INSTALLATION_LOCATIONS_R,
    _CLAIMS_MENU_ASSETS_R,
    _CLAIMS_OBJETOS_TECNICOS_R
} from '@/app/shared/constants/menu-assets.claims';
import {
    _CLAIMS_FIELD_TEAMS_R,
    _CLAIMS_MENU_OPERATIONAL_PLANNING_R,
    _CLAIMS_WFM_PLAN_CONSOLIDATION_R,
    _CLAIMS_WFM_PLANNING_EXCEPTIONS_R,
    _CLAIMS_WFM_PLANNING_HISTORY_R,
    _CLAIMS_WFM_PLANNING_POLICIES_R,
    _CLAIMS_WFM_PRIORITIZATION_POLICIES_R,
    _CLAIMS_WFM_SCHEDULES_R,
    _CLAIMS_WFM_SERVICE_ASSIGNMENT_R
} from '@/app/shared/constants/menu-operational-plan.claims';
import {
    _CLAIMS_MENU_ORDERS_R,
    _CLAIMS_WO_CLOSURE_VALIDATION_R,
    _CLAIMS_WO_ORDER_CONTROLS_R,
    _CLAIMS_WO_ORDERS_CLOSED_R,
    _CLAIMS_WO_ORDERS_R,
    _CLAIMS_WO_TECHNICAL_NOTES_R
} from '@/app/shared/constants/menu-orders.claims';
import {
    claimRead as hdkClaimRead,
    HDK_CLAIM,
    HDK_ISSUES_CLAIM
} from '@/app/shared/constants/hdk.claims';
/**
 * Sidebar navigation tree for SIGOS Manutenção.
 *
 * Structure follows "Mapa do Site — Navegação do SIGOS Manutenção" v3 (31/07/2026):
 * Início · Ordens & Notas · Planejamento Operacional · Ativos · Materiais ·
 * Monitoramento · Análises & Relatórios · Configurações · Administração · Ajuda.
 *
 * Items without a `role`/`role` claim on file are either brand-new navigation
 * entries pointing at `FeatureStubComponent` placeholder routes (no claim exists
 * yet — see `src/app.routes.ts`), or group nodes that stay visible as long as at
 * least one child is visible (see `filterMenuByRoles` in `app.menu.ts`). Existing
 * screens keep the same claims/routes they already had; only their place in the
 * menu changed.
 */
export interface AppMenuModelItem {
    label?: string;
    labelKey?: string;
    icon?: string;
    path?: string;
    routerLink?: string[];
    url?: string | string[];
    target?: string;
    separator?: boolean;
    role?: string[];
    /** Marks the item as non-navigable (used for `[F2]` / Fase 2 items that are visible but not yet clickable). */
    disabled?: boolean;
    /** CSS class applied to the item's `<li>`; used to render the "F2" badge without touching the menu template. */
    badgeClass?: string;
    items?: AppMenuModelItem[];
    [key: string]: unknown;
}

const HOME_MENU: AppMenuModelItem = {
    labelKey: 'MENU.HOME.ROOT',
    icon: 'pi pi-fw pi-home',
    path: '/home',
    items: [
        {
            labelKey: 'MENU.HOME.DAILY_PANEL',
            routerLink: ['/']
        },
        // {
        //     labelKey: 'MENU.HOME.PANEL_BY_ROLE',
        //     disabled: true,
        //     badgeClass: 'menu-item-f2'
        // }
    ]
};

const ORDERS_NOTES_MENU: AppMenuModelItem = {
    labelKey: 'MENU.ORDERS_NOTES.ROOT',
    icon: 'pi pi-fw pi-clipboard',
    path: '/orders-notes',
    items: [
        {
            labelKey: 'MENU.ORDERS_NOTES.ORDERS_LIST',
            routerLink: ['/wo-orders'],
            role: [..._CLAIMS_MENU_ORDERS_R, _CLAIMS_WO_ORDERS_R]            
        },
        {
            labelKey: 'MENU.ORDERS_NOTES.CLOSED_ORDERS',
            disabled: true,
            routerLink: ['/wo-orders-closed'],
            role: [..._CLAIMS_MENU_ORDERS_R, _CLAIMS_WO_ORDERS_CLOSED_R]            
        },
        {
            labelKey: 'MENU.ORDERS_NOTES.TECHNICAL_NOTES',
            disabled: true,
            routerLink: ['/wo-technical-notes'],
            role: [..._CLAIMS_MENU_ORDERS_R, _CLAIMS_WO_TECHNICAL_NOTES_R]            
        },
        {
            labelKey: 'MENU.ORDERS_NOTES.STATUS_PANEL',
            routerLink: ['/wo-order-controls'],
            role: [..._CLAIMS_MENU_ORDERS_R, _CLAIMS_WO_ORDER_CONTROLS_R]
        },
        {
            labelKey: 'MENU.ORDERS_NOTES.CLOSURE_VALIDATION',
            disabled: true,
            routerLink: ['/wo-closure-validation'],
            role: [..._CLAIMS_MENU_ORDERS_R, _CLAIMS_WO_CLOSURE_VALIDATION_R]            
        }
    ]
};

const OPERATIONAL_PLANNING_MENU: AppMenuModelItem = {
    labelKey: 'MENU.OPERATIONAL_PLANNING.ROOT',
    icon: 'pi pi-fw pi-calendar',
    path: '/operational-planning',
    items: [
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.POLICIES',
            routerLink: ['/wfm-planning-policies'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_PLANNING_POLICIES_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.PRIORITIZATION',
            routerLink: ['/wfm-prioritization-policies'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_PRIORITIZATION_POLICIES_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.TEAMS',
            routerLink: ['/field-teams'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_FIELD_TEAMS_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.AVAILABILITY',
            routerLink: ['/wfm-schedules'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_SCHEDULES_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.EXCEPTIONS',
            routerLink: ['/wfm-service-assignment'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_SERVICE_ASSIGNMENT_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.EXCEPTIONS',
            routerLink: ['/wfm-service-assignment'],
            role: ['WFM-SERVICE-ASSIGNMENT-R', 'WFM-SERVICE-ASSIGNMENT-W', 'SYS-ADMIN']
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.EXCEPTIONS',
            disabled: true,
            routerLink: ['/wfm-planning-exceptions'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_PLANNING_EXCEPTIONS_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.CONSOLIDATION',
            disabled: true,
            routerLink: ['/wfm-plan-consolidation'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_PLAN_CONSOLIDATION_R]
        },
        {
            labelKey: 'MENU.OPERATIONAL_PLANNING.HISTORY',
            disabled: true,
            routerLink: ['/wfm-planning-history'],
            role: [..._CLAIMS_MENU_OPERATIONAL_PLANNING_R, _CLAIMS_WFM_PLANNING_HISTORY_R]
        }
    ]
};

const ASSETS_MENU: AppMenuModelItem = {
    labelKey: 'MENU.ASSETS.ROOT',
    icon: 'pi pi-fw pi-wrench',
    path: '/assets',
    items: [

        {
            labelKey: 'MENU.ASSETS.TECHNICAL_OBJECTS.ROOT',
            routerLink: ['/consultas/objetos-tecnicos'],
            role: [..._CLAIMS_MENU_ASSETS_R, _CLAIMS_OBJETOS_TECNICOS_R]
        },
        {
            labelKey: 'MENU.ASSETS.TECHNICAL_OBJECTS.INSTALLATION_LOCATIONS',
            routerLink: ['/eqp-installation-locations'],
            role: [..._CLAIMS_MENU_ASSETS_R, _CLAIMS_EQP_INSTALLATION_LOCATIONS_R]
        },
        {
            labelKey: 'MENU.ASSETS.SAP_TABLES.ROOT',
            routerLink: ['/aux-tables'],
            role: [..._CLAIMS_MENU_ASSETS_R, _CLAIMS_AUX_TABLES_R]
        }
    ]
};

const MATERIALS_MENU: AppMenuModelItem = {
    labelKey: 'MENU.MATERIALS.ROOT',
    icon: 'pi pi-fw pi-box',
    path: '/materials',
    items: [
        {
            labelKey: 'MENU.MATERIALS.RESERVATIONS',
            disabled: true,
            routerLink: ['/wo-material-reservations']
        },
        {
            labelKey: 'MENU.MATERIALS.BALANCE',
            disabled: true,
            routerLink: ['/wo-material-balance']
        },
        {
            labelKey: 'MENU.MATERIALS.MONITOR',
            disabled: true,
            routerLink: ['/wo-material-monitor']
        },
        {
            labelKey: 'MENU.MATERIALS.STOCK_CONTROL',
            disabled: true,
            routerLink: ['/wo-stock-control']
        },
        {
            labelKey: 'MENU.MATERIALS.APPLIED',
            disabled: true,
            routerLink: ['/wo-materials-applied']
        },
        {
            labelKey: 'MENU.MATERIALS.CATALOG.ROOT',
            path: '/materials/catalog',
            items: [
                {
                    labelKey: 'MENU.MATERIALS.CATALOG.MATERIALS',
                    disabled: true,
                    routerLink: ['/wo-materials']
                },
                {
                    labelKey: 'MENU.MATERIALS.CATALOG.GROUPS',
                    disabled: true,
                    routerLink: ['/wo-material-groups']
                },
                {
                    labelKey: 'MENU.MATERIALS.CATALOG.KITS',
                    disabled: true,
                    routerLink: ['/wo-material-kits']
                },
                {
                    labelKey: 'MENU.MATERIALS.CATALOG.WAREHOUSES',
                    disabled: true,
                    routerLink: ['/wo-warehouses']
                },
                {
                    labelKey: 'MENU.MATERIALS.CATALOG.OPERATIONS',
                    disabled: true,
                    routerLink: ['/wo-material-operations']
                }
            ]
        }
    ]
};

const MONITORING_MENU: AppMenuModelItem = {
    labelKey: 'MENU.MONITORING.ROOT',
    icon: 'pi pi-fw pi-desktop',
    path: '/monitoring',
    items: [
        {
            labelKey: 'MENU.MONITORING.TEAM_TRACKING',
            disabled: true,
            routerLink: ['/wfm-team-tracking']
        },
        {
            labelKey: 'MENU.MONITORING.GANTT',
            routerLink: ['/gantt'],
            role: ['WFM-GANTT-R','WFM-GANTT-W','MONITORING-R', 'MONITORING-W','SYS-ADMIN']
        },
        {
            labelKey: 'MENU.MONITORING.DAILY_PRODUCTION',
            disabled: true,
            routerLink: ['/wfm-daily-production']
        },
        {
            labelKey: 'MENU.MONITORING.SLA_ALERTS',
            disabled: true,
            routerLink: ['/wfm-sla-alerts']
        },
        {
            labelKey: 'MENU.MONITORING.INTEGRATIONS_DASHBOARD',
            routerLink: ['/monitor-dashboard'],
            role: ['INTEGRATION-API-DOCS-R', 'INT-SAP-R', 'SYS-ADMIN']
        }
    ]
};

const ANALYTICS_MENU: AppMenuModelItem = {
    labelKey: 'MENU.ANALYTICS.ROOT',
    icon: 'pi pi-fw pi-chart-bar',
    path: '/analytics',
    items: [
        {
            labelKey: 'MENU.ANALYTICS.MANAGEMENT_DASHBOARDS',
            disabled: true,
            routerLink: ['/wfm-management-dashboards']

        },
        {
            labelKey: 'MENU.ANALYTICS.OPERATIONAL_REPORTS',
            disabled: true,
            routerLink: ['/wfm-operational-reports']
        },
        {
            labelKey: 'MENU.ANALYTICS.INDEXES',
            disabled: true,
            routerLink: ['/wfm-indexes']
        },
        {
            labelKey: 'MENU.ANALYTICS.CONTRACTOR_MEASUREMENT',
            disabled: true,
            badgeClass: 'menu-item-f2'
        },
        {
            labelKey: 'MENU.ANALYTICS.ORDER_SETTLEMENT',
            disabled: true,
            badgeClass: 'menu-item-f2'
        }
    ]
};

const SETTINGS_MENU: AppMenuModelItem = {
    labelKey: 'MENU.SETTINGS.ROOT',
    icon: 'pi pi-fw pi-cog',
    path: '/settings',
    items: [
        {
            labelKey: 'MENU.SETTINGS.SERVICES.ROOT',
            path: '/settings/services',
            items: [
                {
                    labelKey: 'MENU.SETTINGS.SERVICES.EXECUTION_FLOWS',
                    disabled: true,
                    routerLink: ['/wo-execution-flows']
                },
                {
                    labelKey: 'MENU.SETTINGS.SERVICES.STATUS',
                    routerLink: ['/wo-order-statuses'],
                    role: ['WO-CONFIG-R', 'WO-CONFIG-W', 'WO-ORDER-STATUSES-R', 'WO-ORDER-STATUSES-W', 'SETTINGS_SERVICES-R', 'SETTINGS_SERVICES-W', 'SYS-ADMIN'],
                },
                {
                    labelKey: 'MENU.SETTINGS.SERVICES.CHECKLISTS',
                    path: '/settings/services/checklists',
                    role: ['CKL-TEMPLATES-R', 'CKL-LAYOUT-ITEMS-R', 'SETTINGS_SERVICES-R', 'SETTINGS_SERVICES-W', 'SYS-ADMIN'],
                    items: [
                        {
                            labelKey: 'MENU.CONFIGURATION.CHECKLIST.TEMPLATES',
                            routerLink: ['/ckl-templates'],
                            role: ['CKL-TEMPLATES-R', 'SETTINGS_SERVICES-R', 'SETTINGS_SERVICES-W', 'SYS-ADMIN']
                        },
                        {
                            labelKey: 'MENU.CONFIGURATION.CHECKLIST.LAYOUT_ITEMS',
                            routerLink: ['/ckl-layout-items'],
                            role: ['CKL-LAYOUT-ITEMS-R', 'SEC-ADM-R', 'SYS-ADMIN']
                        }
                    ]
                },
                {
                    labelKey: 'MENU.SETTINGS.SERVICES.SERVICE_CHARACTERISTICS',
                    routerLink: ['/wo-service-characteristics'],
                    role: ['WO-CONFIG-R', 'WO-ORDER-TYPES-R', 'SYS-ADMIN']
                },
                {
                    labelKey: 'MENU.SETTINGS.SERVICES.SERVICE_CLASSES',
                    routerLink: ['/wo-service-classes'],
                    role: ['WO-CONFIG-R', 'WO-ORDER-TYPES-R', 'SYS-ADMIN']
                },
                {
                    labelKey: 'MENU.SETTINGS.SERVICES.SERVICE_FLOW',
                    routerLink: ['/fluxo-servicos'],
                    role: ['WO-CONFIG-R', 'WO-CONFIG-W', 'WF-ORDER-FLOWS-R', 'WF-ORDER-FLOWS-W', 'SETTINGS_SERVICES-R', 'SETTINGS_SERVICES-W', 'SYS-ADMIN']
                },

                {
                    labelKey: 'MENU.SETTINGS.SERVICES.SERVICE_CONFIG',
                    routerLink: ['/wo-service-config'],
                    role: ['WO-SERVICE-CONFIGURATIONS-R', 'WO-R', 'SYS-ADMIN']
                },
            ]
        },
        {
            labelKey: 'MENU.SETTINGS.CONTRACTORS.ROOT',
            path: '/settings/contractors',
            items: [
                {
                    labelKey: 'MENU.SETTINGS.CONTRACTORS.COMPANIES',
                    routerLink: ['/sec-contractor-companies'],
                    role: ['SEC-CONTRACTOR-COMPANIES-R', 'SEC-R', 'SYS-ADMIN']
                },                
                {
                    labelKey: 'MENU.SETTINGS.CONTRACTORS.EMPLOYEES_IMPORT',
                    disabled: false,
                    routerLink: ['/wfm-contractor-employees-import']
                },               
                {
                    labelKey: 'MENU.SETTINGS.CONTRACTORS.SCHEDULES_IMPORT',
                    disabled: false,
                    routerLink: ['/wfm-contractor-schedules-import']
                },
                {
                    labelKey: 'MENU.SETTINGS.CONTRACTORS.SHIFT_IMPORT',
                    disabled: false,
                    routerLink: ['/wfm-contractor-shifts-import']
                },
                {
                    labelKey: 'MENU.SETTINGS.CONTRACTORS.POSITIONS',
                    disabled: true,
                    routerLink: ['/employee-positions'],
                    role: ['WFM-EMPLOYEE-POSITIONS-R']
                }
            ]
        },
        {
            labelKey: 'MENU.SETTINGS.ORGANIZATION.ROOT',
            path: '/settings/organization',
            items: [
                {
                    labelKey: 'MENU.SETTINGS.ORGANIZATION.STRUCTURE',
                    routerLink: ['/organization-structures'],
                    role: ['SEC-ORGANIZATION-STRUCTURES-R']
                },
                {
                    labelKey: 'MENU.SETTINGS.ORGANIZATION.EMPLOYEES',
                    routerLink: ['/employees'],
                    role: ['WFM-EMPLOYEES-R', 'ADMIN','SYS-ADMIN']
                },
                {
                    labelKey: 'MENU.SETTINGS.POSITIONS.EMPLOYEES',
                    routerLink: ['/employee-positions'],
                    role: ['WFM-EMPLOYEE-POSITIONS-R', 'ADMIN', 'SYS-ADMIN']
                },
                {
                    labelKey: 'MENU.SETTINGS.ORGANIZATION.VEHICLES',
                    disabled: true,
                    routerLink: ['/wfm-vehicles']
                },
                {
                    labelKey: 'MENU.SETTINGS.ORGANIZATION.PROJECTS',
                    disabled: true,
                    routerLink: ['/wfm-projects']
                },

            ]
        },
        {
            labelKey: 'MENU.SETTINGS.CALENDAR.ROOT',
            path: '/settings/calendar',
            items: [
                {
                    labelKey: 'MENU.SETTINGS.CALENDAR.HOLIDAYS_NATIONAL',
                    routerLink: ['/utl-holidays'],
                    role: ['UTL-HOLIDAYS-R', 'SYS-ADMIN']
                },
                {
                    labelKey: 'MENU.SETTINGS.CALENDAR.HOLIDAYS_BY_LOCATION',
                    routerLink: ['/utl-holidays-locations']
                }
            ]
        }
    ]
};

const ADMIN_MENU: AppMenuModelItem = {
    labelKey: 'MENU.ADMIN.ROOT',
    icon: 'pi pi-fw pi-shield',
    path: '/administration',
    items: [
        {
            labelKey: 'MENU.ADMIN.USERS.ROOT',
            path: '/administration/users',
            items: [
                {
                    labelKey: 'MENU.ADMIN.USERS.LIST',
                    routerLink: ['/users'],
                    role: ['SEC-USERS-R']
                },
                {
                    labelKey: 'MENU.ADMIN.USERS.ROLES',
                    routerLink: ['/roles'],
                    role: ['SEC-USERS-R']
                },
                {
                    labelKey: 'MENU.ADMIN.USERS.PERMISSIONS',
                    routerLink: ['/claims'],
                    role: ['SEC-USERS-R']
                },
            ]
        },
        {
            labelKey: 'MENU.ADMIN.LICENSES.ROOT',
            path: '/administration/licenses',
            items: [
                {
                    labelKey: 'MENU.ADMIN.LICENSES.CONFIG',
                    disabled: true,
                    routerLink: ['/sec-license-config']
                },
                {
                    labelKey: 'MENU.ADMIN.LICENSES.CONTROL',
                    disabled: false,
                    routerLink: ['/sec-license-control']
                }
            ]
        },
        {
            labelKey: 'MENU.ADMIN.MESSAGES',
            disabled: true,
            routerLink: ['/sec-user-messages']
        },
        {
            labelKey: 'MENU.ADMIN.SYSTEM.ROOT',
            path: '/administration/system',
            items: [
                    {
                        labelKey: 'MENU.SETTINGS.ORGANIZATION.COMPANIES',
                        routerLink: ['/company-detail'],
                        role: ['SEC-COMPANIES-R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.ORGANIZATION.EMPLOYEE_ROLE',
                        routerLink: ['/employee-roles'],
                        role: ['WFM-EMPLOYEE-ROLES-R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.ORGANIZATION.EMPLOYEE_SHIFT',
                        routerLink: ['/employee-shifts'],
                        role: ['WFM-EMPLOYEE-SHIFTS-R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.ORGANIZATION.SKILLS',
                        routerLink: ['/skills'],
                        role: ['WFM-SKILLS-R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.ORGANIZATION.EMPLOYEE_STATUS',
                        routerLink: ['/employee-statuses'],
                        role: ['WFM-EMPLOYEE-STATUSES-R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.ORGANIZATION.TEAM_STATUS',
                        routerLink: ['/field-team-statuses'],
                        role: ['WFM_FIELD_TEAM_STATUSES_R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.SERVICES.SERVICE_FLOW',
                        routerLink: ['/wfm-order-checklists-items'],
                        disabled: true,
                        role: ['WF-ORDER-FLOWS-R', 'WO-CONFIG-R', 'SYS-ADMIN']
                    },
                    {
                        labelKey: 'MENU.SETTINGS.SERVICES.FLOW_TYPE',
                        routerLink: ['/wf-flow-types'],
                        role: ['WF-FLOW-TYPES-R', 'WO-CONFIG-R', 'SYS-ADMIN']
                    },
            ]
        }
    ]
};

const HELP_MENU: AppMenuModelItem = {
    labelKey: 'MENU.HELP.ROOT',
    icon: 'pi pi-fw pi-question-circle',
    path: '/help',
    items: [
        {
            labelKey: 'MENU.HELP.DOCUMENTATION',
            disabled: true,
            routerLink: ['/help-documentation']
        },
        {
            labelKey: 'MENU.HELP.CHANGELOG',
            disabled: true,
            routerLink: ['/help-changelog']
        },
        {
            labelKey: 'MENU.HELP.ISSUE',
            routerLink: ['/hdk-issues'],
            role: [hdkClaimRead(HDK_ISSUES_CLAIM), hdkClaimRead(HDK_CLAIM), 'SYS-ADMIN']
        },
        {
            labelKey: 'MENU.HELP.INTEGRATION',
            routerLink: ['/integration-api-docs'],
            role: ['INTEGRATION-API-DOCS-R', 'INT-SAP-R', 'SYS-ADMIN']
        },
    ]
};

export {
    HOME_MENU,
    ORDERS_NOTES_MENU,
    OPERATIONAL_PLANNING_MENU,
    ASSETS_MENU,
    MATERIALS_MENU,
    MONITORING_MENU,
    ANALYTICS_MENU,
    SETTINGS_MENU,
    ADMIN_MENU,
    HELP_MENU
};
